import { type Href, Redirect, usePathname } from "expo-router";
import { type ReactNode, useState } from "react";

import { resolveRouteAccess } from "@/session/access";
import { useSession } from "@/session/hooks";
import { usePlatform } from "@/session/platform";
import type { Area, SessionState } from "@/session/types";

import { LoadingScreen } from "./LoadingScreen";
import NotFoundScreen from "./NotFoundScreen";
import { UnsupportedRoleScreen } from "./UnsupportedRoleScreen";

type FrozenRedirect = { session: SessionState; to: string };

/**
 * Applies the route-access rules (data-model §2) for a group layout (FR-013 to FR-016). UX only
 * (R-7): the server enforces access. Children render only on `allow`.
 */
export function RouteGuard({
  area,
  children,
}: {
  area: Area;
  children: ReactNode;
}) {
  const session = useSession();
  const platform = usePlatform();
  const pathname = usePathname();
  const access = resolveRouteAccess(session, area, platform, pathname);

  // Keep the first redirect target for this session state. While the redirect is under way the
  // router can briefly report another address (on the web, "/" from the tabs' initial route), and
  // a second target would drop the returnTo of the first (FR-013, US1-6).
  const [frozen, setFrozen] = useState<FrozenRedirect | null>(null);
  const wanted =
    access.kind === "redirect"
      ? frozen?.session === session
        ? frozen
        : { session, to: access.to }
      : null;
  if (wanted !== frozen) setFrozen(wanted);

  switch (access.kind) {
    case "allow":
      return <>{children}</>;
    case "loading":
      return <LoadingScreen />;
    case "redirect":
      return <Redirect href={(wanted?.to ?? access.to) as Href} />;
    case "notFound":
      return <NotFoundScreen />;
    case "unsupported":
      return <UnsupportedRoleScreen reason={access.reason} />;
  }
}
